import type { OutboxItem, OutboxStore } from './types'

/**
 * An in-memory queue with the same semantics as the IndexedDB one.
 *
 * 🔴 THIS IS NOT ONLY A TEST DOUBLE. When IndexedDB is unavailable — Safari
 * private mode, a locked-down school profile, a browser that denies storage —
 * the outbox falls back to this. Items are then lost on reload, which is worse
 * than durable and **far** better than a child's session failing to start
 * because storage was denied. Same reasoning as the device store's null-storage
 * path (§4.5): a device that cannot persist still plays.
 */
export function createMemoryStore(): OutboxStore {
  const items = new Map<number, OutboxItem>()
  const failedItems: OutboxItem[] = []
  // Monotonic, never reused — FIFO order is id order, exactly like IndexedDB's
  // autoIncrement, so the two stores cannot drift on the property that matters.
  let nextId = 1

  const fifo = (): OutboxItem[] => [...items.values()].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))

  return {
    add(item) {
      const id = nextId++
      items.set(id, { ...item, id })
      return Promise.resolve(id)
    },
    head(limit) {
      return Promise.resolve(fifo().slice(0, limit))
    },
    update(item) {
      if (item.id !== undefined && items.has(item.id)) items.set(item.id, { ...item })
      return Promise.resolve()
    },
    remove(id) {
      items.delete(id)
      return Promise.resolve()
    },
    removeMany(ids) {
      for (const id of ids) items.delete(id)
      return Promise.resolve()
    },
    count() {
      return Promise.resolve(items.size)
    },
    all() {
      return Promise.resolve(fifo())
    },
    fail(item) {
      if (item.id !== undefined) items.delete(item.id)
      failedItems.push({ ...item })
      return Promise.resolve()
    },
    failed() {
      return Promise.resolve([...failedItems])
    },
    restoreFailed() {
      const moved = failedItems.length
      // Appended in their original order, at the tail, with attempts reset —
      // a retry a teacher asked for is a new decision, not a continuation of
      // the automatic one that gave up.
      for (const item of failedItems.splice(0, failedItems.length)) {
        const id = nextId++
        items.set(id, { ...item, id, attempts: 0 })
      }
      return Promise.resolve(moved)
    },
  }
}
